import heroImageUrl from "@/assets/images/hero-tutora-cachorro.jpg"
import { useSiteContent } from "@/shared/content/SiteContentProvider"
import { scrollToAnchor } from "@/shared/lib/navigation"
import { openWhatsApp } from "@/shared/lib/whatsapp"
import { Button } from "@/shared/ui/Button"
import { ArrowRight, HeartFilled, Icon, StarOutline } from "@/shared/ui/icons"

export function HeroSection() {
  const { heroIndicators } = useSiteContent()

  return (
    <section
      id="inicio"
      className="relative overflow-hidden bg-off-white pt-10 pb-0 lg:pt-20 lg:pb-0 min-h-[90vh] flex items-center"
    >
      {/* Camadas decorativas: dois halos radiais e uma malha sutil. */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(46,55,36,0.06) 0%, transparent 65%)" }}
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 -left-16 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(201,150,59,0.07) 0%, transparent 65%)" }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.018]"
        style={{
          backgroundImage:
            "linear-gradient(#2E3724 1px, transparent 1px), linear-gradient(90deg, #2E3724 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* ── Texto ─────────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-7 lg:gap-9 lg:py-16">
            <div className="animate-fade-slide-up" style={{ animationDelay: "0.05s" }}>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-verde-profundo text-dourado-claro text-xs font-semibold tracking-widest uppercase shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-dourado inline-block animate-pulse" />
                Saúde preventiva para pets
              </span>
            </div>

            <div className="animate-fade-slide-up" style={{ animationDelay: "0.15s" }}>
              <h1 className="text-[32px] sm:text-[44px] lg:text-[64px] font-semibold text-verde-profundo leading-[1.04] tracking-[-0.03em]">
                Prevenção <span className="shimmer-gold">salva vidas.</span>
                <br />
                <span className="text-verde-medio">Cuide do seu</span>
                <br />
                <span className="text-verde-profundo">melhor amigo.</span>
              </h1>
            </div>

            <p
              className="text-lg text-verde-profundo/65 leading-relaxed max-w-lg animate-fade-slide-up"
              style={{ animationDelay: "0.25s" }}
            >
              Planos de cuidado veterinário para acompanhar a saúde do seu cão ou gato em diferentes fases da
              vida.
            </p>

            <div className="flex flex-wrap gap-3 animate-fade-slide-up" style={{ animationDelay: "0.35s" }}>
              <Button
                variant="primary"
                size="lg"
                onClick={() => openWhatsApp()}
                className="shadow-lg shadow-verde-profundo/20 hover:shadow-xl hover:shadow-verde-profundo/25 hover:-translate-y-0.5"
                icon={<ArrowRight />}
              >
                Contratar agora
              </Button>
              <Button variant="secondary" size="lg" onClick={() => scrollToAnchor("#planos")}>
                Conhecer os planos
              </Button>
            </div>

            <div
              className="flex flex-wrap gap-x-3 sm:gap-x-6 gap-y-2 pt-1 animate-fade-slide-up"
              style={{ animationDelay: "0.45s" }}
            >
              {heroIndicators.map((indicator) => (
                <div
                  key={indicator.label}
                  className="flex items-center gap-2 text-sm text-verde-profundo/60 font-medium"
                >
                  <span className="text-dourado-escuro">
                    <Icon name={indicator.icon} />
                  </span>
                  {indicator.label}
                </div>
              ))}
            </div>

            <p
              className="text-xs text-verde-profundo/40 leading-relaxed max-w-md border-l-2 border-dourado/40 pl-3 animate-fade-slide-up"
              style={{ animationDelay: "0.5s" }}
            >
              Atendimento com disponibilidade conforme a rede credenciada de cada região.
            </p>
          </div>

          {/* ── Foto ──────────────────────────────────────────────────────── */}
          <div className="relative flex justify-center lg:justify-end animate-scale-in" style={{ animationDelay: "0.2s" }}>
            <div
              className="absolute -top-6 -right-6 w-44 h-44 rounded-full border border-dourado/20 pointer-events-none"
              aria-hidden="true"
            />
            <div
              className="absolute -top-12 -right-12 w-28 h-28 rounded-full border-2 border-verde-profundo/8 pointer-events-none"
              aria-hidden="true"
            />
            <div
              className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full border border-dourado/15 pointer-events-none"
              aria-hidden="true"
            />

            <div className="relative w-full aspect-[3/4] sm:aspect-[4/5] max-w-md lg:max-w-none rounded-[32px] sm:rounded-[40px] lg:rounded-[48px] overflow-hidden shadow-2xl shadow-verde-profundo/20">
              {/* Fundo verde por tras da foto: enquanto o JPEG carrega, o buraco
                  no layout fica na cor da marca, e nao branco. */}
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(150deg, #405439 0%, #2E3724 100%)" }}
                aria-hidden="true"
              />
              <img
                src={heroImageUrl}
                alt="Tutora sorrindo com seu cachorro, feliz e saudável"
                // A imagem do hero e o LCP da pagina: prioridade alta e sem
                // lazy, ao contrario de todo o resto.
                fetchPriority="high"
                decoding="async"
                width={800}
                height={1000}
                className="relative w-full h-full object-cover object-top"
                style={{ filter: "saturate(1.08) brightness(0.96)" }}
              />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(to top, rgba(46,55,36,0.55) 0%, transparent 45%)" }}
                aria-hidden="true"
              />
              <div
                className="absolute top-0 left-0 right-0 h-1"
                style={{
                  background: "linear-gradient(90deg, transparent, #C9963B 40%, #D8B36A 60%, transparent)",
                }}
                aria-hidden="true"
              />
            </div>

            <div className="absolute bottom-6 left-6 bg-white/96 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-xl flex items-center gap-3 border border-verde-profundo/6">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-dourado to-dourado-claro flex items-center justify-center shrink-0 shadow-sm text-white">
                <HeartFilled />
              </div>
              <p className="text-xs font-bold text-verde-profundo leading-none">Plano Essencial</p>
            </div>

            <div className="absolute top-8 -left-4 bg-verde-profundo rounded-2xl px-4 py-3 shadow-xl hidden lg:flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-dourado/20 flex items-center justify-center text-dourado">
                <StarOutline />
              </div>
              <div>
                <p className="text-[11px] font-bold text-dourado-claro leading-none">Cuidado real</p>
                <p className="text-[10px] text-off-white/50 mt-0.5">Ética e transparência</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
